import { BreadcrumbItem } from '@sdx/fe-utils';
import { create } from 'zustand';

export type PendingChangesKey =
  | 'favApps'
  | 'workforceSizing'
  | 'externalLinks'
  | 'integratedManagement';

export type NavigationDialogConfig = {
  titleKey: string;
  messageKey: string;
  secondaryLabelKey?: string;
  primaryLabelKey: string;
  onSecondary?: () => void;
  onPrimary?: () => void;
  isPrimaryAsync?: boolean;
  isPrimaryLoading?: boolean;
};

export type NavigationState = {
  breadcrumbs: Record<string, BreadcrumbItem>;
  initialized: boolean;

  showPendingChangesDialog: boolean;
  pendingNavigationPath: string | null;
  dialogOpenedAt: number | null;

  pendingChangesByFeature: Partial<Record<PendingChangesKey, boolean>>;
  hasPendingChanges: boolean;

  dialogConfig: NavigationDialogConfig | null;
  dialogConfigs: Partial<Record<PendingChangesKey, NavigationDialogConfig>>;

  addBreadcrumb: (breadcrumb: BreadcrumbItem) => void;
  resetBreadcrumbs: () => void;
  setBreadcrumbPath: (path: BreadcrumbItem[]) => void;

  blockNavigation: (targetPath: string) => void;
  cancelNavigation: () => void;
  confirmNavigation: (navigate: (path: string) => void) => void;
  setPendingChanges: (key: PendingChangesKey, isDirty: boolean) => void;
  registerDialogConfig: (key: PendingChangesKey, config: NavigationDialogConfig) => void;
  updateDialogConfig: (key: PendingChangesKey, partial: Partial<NavigationDialogConfig>) => void;
};

const useNavigationStore = create<NavigationState>((set, get) => ({
  breadcrumbs: {
    homepage: { key: 'homepage', label: 'Home page', path: '/home' },
  },
  initialized: false,

  showPendingChangesDialog: false,
  pendingNavigationPath: null,
  dialogOpenedAt: null,

  pendingChangesByFeature: {},
  hasPendingChanges: false,

  dialogConfig: null,
  dialogConfigs: {},

  addBreadcrumb: (breadcrumb) =>
    set((state) => {
      const exists = state.breadcrumbs[breadcrumb.key] !== undefined;
      if (exists) return state;
      return { breadcrumbs: { ...state.breadcrumbs, [breadcrumb.key]: breadcrumb } };
    }),

  setBreadcrumbPath: (path: BreadcrumbItem[]) =>
    set(() => {
      const breadcrumbs: Record<string, BreadcrumbItem> = {};
      path.forEach((item) => {
        breadcrumbs[item.key] = item;
      });
      return { breadcrumbs, initialized: true };
    }),

  resetBreadcrumbs: () =>
    set({
      breadcrumbs: { homepage: { key: 'homepage', label: 'Home page', path: '/home' } },
      initialized: true,
    }),

  blockNavigation: (targetPath: string) => {
    const { pendingChangesByFeature, dialogConfigs } = get();
    const activeKey = (Object.keys(pendingChangesByFeature) as PendingChangesKey[]).find(
      (k) => pendingChangesByFeature[k] === true,
    );
    set({
      showPendingChangesDialog: true,
      pendingNavigationPath: targetPath,
      dialogOpenedAt: Date.now(),
      dialogConfig: activeKey ? (dialogConfigs[activeKey] ?? null) : null,
    });
  },

  cancelNavigation: () => {
    set({
      showPendingChangesDialog: false,
      pendingNavigationPath: null,
      dialogOpenedAt: null,
      dialogConfig: null,
    });
  },

  confirmNavigation: (navigate) => {
    const { pendingNavigationPath } = get();
    set({ showPendingChangesDialog: false, dialogOpenedAt: null, dialogConfig: null });
    if (pendingNavigationPath) {
      navigate(pendingNavigationPath);
      set({ pendingNavigationPath: null });
    }
  },

  setPendingChanges: (key, isDirty) => {
    set((state) => {
      const next = { ...state.pendingChangesByFeature, [key]: isDirty };
      return {
        pendingChangesByFeature: next,
        hasPendingChanges: Object.values(next).some(Boolean),
      };
    });
  },

  registerDialogConfig: (key, config) => {
    set((state) => ({
      dialogConfigs: { ...state.dialogConfigs, [key]: config },
    }));
  },

  updateDialogConfig: (key, partial) => {
    set((state) => {
      const existing = state.dialogConfigs[key];
      return {
        dialogConfigs: existing
          ? { ...state.dialogConfigs, [key]: { ...existing, ...partial } }
          : state.dialogConfigs,
        dialogConfig: state.dialogConfig
          ? { ...state.dialogConfig, ...partial }
          : state.dialogConfig,
      };
    });
  },
}));

export default useNavigationStore;
